"use client"

import { useCallback, useEffect, useRef } from "react"

import { useLocale, useTranslations } from "next-intl"

import { ImpressionList } from "@/components/marketplace/impression-target"
import { ListingPageLinks } from "@/components/marketplace/listing/listing-page-links"
import { ProductCard, ProductCardSkeleton } from "@/components/marketplace/product/product-card"
import type { Product } from "@/components/marketplace/types"
import { Button, CheckIcon, ChevronIcon, Grid, Heading, Row, Stack, Text } from "@/components/ui"
import { opensElsewhere } from "@/lib/helpers/link-click"
import type { ListingPagination } from "@/lib/listing-pagination"
import { useBrowseValue } from "@/platform/app-mode/use-browse-value"

interface InfiniteProductListProps {
  products: Product[]
  renderedQuery?: string
  pageSize?: number
  autoLoad?: boolean
  title?: string | null
  // External-source mode: the caller owns paging — `products` is everything
  // loaded so far and `onLoadMore` fetches the next page.
  totalCount?: number
  hasMore?: boolean
  loading?: boolean
  onLoadMore?: () => void
  previousPageHref?: string
  nextPageHref?: string
  // bare drops the section chrome (background, vertical padding, max-w-page/px-gut)
  // so a parent that already owns the column — the listing shell — can place the grid.
  bare?: boolean
  onProductClick?: (product: Product, index: number) => void
  // External-source mode only: the fetched pages behind `products`, in order. Each page is a
  // separately served list, so each gets its own wrapper and serve ID.
  pages?: { serveId?: string; products: Product[] }[]
  pagination?: ListingPagination
}

// Auto-load pauses after this many scroll-triggered pages; the explicit
// "Cargar más" press re-arms it, so the footer stays reachable.
const AUTO_LOADS_PER_CYCLE = 3

const PRELOADED_CARDS = 2

const PAST_END_REACH_PX = 100000

export const InfiniteProductList = ({
  products,
  renderedQuery,
  pageSize = 8,
  autoLoad = false,
  title,
  totalCount,
  hasMore: externalHasMore,
  loading = false,
  onLoadMore,
  previousPageHref,
  nextPageHref,
  bare = false,
  onProductClick,
  pages,
  pagination,
}: InfiniteProductListProps) => {
  const t = useTranslations("marketplace.product")
  const locale = useLocale()
  const heading = title === undefined ? t("allProducts") : title
  const [shown, setShown] = useBrowseValue(`products:${products[0]?.id ?? "empty"}`, Math.min(pageSize, products.length), renderedQuery)
  const [autoLoadsLeft, setAutoLoadsLeft] = useBrowseValue(`autoload:${products[0]?.id ?? "empty"}`, AUTO_LOADS_PER_CYCLE, renderedQuery)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const external = onLoadMore !== undefined
  const visible = external ? products : products.slice(0, shown)
  const total = totalCount ?? products.length
  const hasMore = external ? (externalHasMore ?? false) : shown < products.length
  const settledCount = useRef(visible.length)
  useEffect(() => {
    settledCount.current = visible.length
  }, [visible.length])
  const loadMore = useCallback(() => {
    if (loading) return
    if (onLoadMore) {
      onLoadMore()
      return
    }
    setShown((current) => Math.min(current + pageSize, products.length))
  }, [loading, onLoadMore, pageSize, products.length, setShown])
  const pressLoadMore = () => {
    loadMore()
    setAutoLoadsLeft(AUTO_LOADS_PER_CYCLE)
  }
  useEffect(() => {
    if (!autoLoad || !hasMore || autoLoadsLeft === 0) return
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || loading) return
        loadMore()
        setAutoLoadsLeft((left) => left - 1)
      },
      { rootMargin: `${PAST_END_REACH_PX}px 0px ${window.innerHeight}px 0px` }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [shown, hasMore, autoLoad, autoLoadsLeft, loading, loadMore, setAutoLoadsLeft])
  const renderCard = (product: Product, index: number) => {
    const fresh = index >= settledCount.current
    return (
      <div
        key={`${product.id}-${index}`}
        style={fresh ? { animationDelay: `${Math.min(index - settledCount.current, 8) * 70}ms` } : undefined}
        className={fresh ? "cv-card motion-safe:animate-rise-in h-full" : "cv-card h-full"}
      >
        <ProductCard product={product} priority={index < PRELOADED_CARDS} onClick={onProductClick ? () => onProductClick(product, index) : undefined} />
      </div>
    )
  }
  const body = (
    <>
      {heading && (
        <Row align="baseline" justify="between" gap="300" className="mb-400">
          <Heading as="h3" variant="xl" family="display" tone="default" balance className="leading-none font-extrabold">
            {heading}
          </Heading>
          <Text
            as="span"
            variant="bodySm"
            family="display"
            tone="inherit"
            numeric
            className="bg-palette-soft text-palette-on-soft rounded-full px-300 py-150 font-bold whitespace-nowrap"
          >
            {(pagination?.offset ?? 0) + visible.length} de {total}
          </Text>
        </Row>
      )}
      {previousPageHref && (
        <Row justify="center" className="mb-500">
          <Button variant="outline" asChild>
            <a href={previousPageHref} rel="prev">
              {t("previousPage")}
            </a>
          </Button>
        </Row>
      )}
      {/* Its own container: a sidebar appearing beside the grid must change the column count. */}
      <div className="@container">
        <Grid cols={2} gap="500" className="@xl:grid-cols-3 @3xl:gap-y-750 @4xl:grid-cols-4 @7xl:grid-cols-5">
          {external && pages
            ? pages.map((page, pageIndex) => {
                const offset = pages.slice(0, pageIndex).reduce((sum, earlier) => sum + earlier.products.length, 0)
                return (
                  <ImpressionList key={page.serveId ?? `page-${pageIndex}`} serveIds={[page.serveId]} region className="contents">
                    {page.products.map((product, index) => renderCard(product, offset + index))}
                  </ImpressionList>
                )
              })
            : visible.map(renderCard)}
          {loading && Array.from({ length: pageSize }).map((_, index) => <ProductCardSkeleton key={`skeleton-${index}`} />)}
        </Grid>
      </div>
      {hasMore ? (
        <Stack align="center" gap="200" className="relative mt-600">
          <div ref={sentinelRef} className="absolute -top-40 size-px" aria-hidden />
          <Text as="span" variant="caption" family="display" tone="muted" numeric className="tracking-widest uppercase">
            {t("seenSoFar", { shown: ((pagination?.offset ?? 0) + visible.length).toLocaleString(locale), total: total.toLocaleString(locale) })}
          </Text>
          {nextPageHref ? (
            <Button variant="outline" asChild loading={loading} className="gap-150">
              <a
                href={nextPageHref}
                rel="next"
                onClick={(event) => {
                  if (opensElsewhere(event)) return
                  event.preventDefault()
                  pressLoadMore()
                }}
              >
                {t("loadMore")}
                <ChevronIcon size={16} className="rotate-90" />
              </a>
            </Button>
          ) : (
            <Button variant="outline" onClick={pressLoadMore} loading={loading} className="gap-150" iconRight={<ChevronIcon size={16} className="rotate-90" />}>
              {t("loadMore")}
            </Button>
          )}
        </Stack>
      ) : (
        <Row justify="center" gap="150" className="border-line-subtle font-display text-body-sm text-fg-success-deep mt-600 border-y border-dashed py-400 font-semibold">
          <CheckIcon size={18} /> <span className="tabular-nums">{pagination?.offset ? t("reachedEnd") : t("seenAll", { total })}</span>
        </Row>
      )}
      {pagination && (
        <div className="mt-400">
          <ListingPageLinks pagination={pagination} />
        </div>
      )}
    </>
  )

  if (bare) return body

  return (
    <div className="bg-surface @container py-600">
      <div className="max-w-page px-gut mx-auto w-full">{body}</div>
    </div>
  )
}
